import { omit } from 'es-toolkit';
import { ExpandableTextarea } from './ExpandableTextarea';
import { Input } from './inputs';
import { InputWrapper } from './InputWrapper';
import { Select } from './selects';
import { Textarea } from './Textarea';
import { Toggle } from './Toggle';
import { cn } from '../utils';

/** Narrow view of the JSON Schema z.toJSONSchema() produces — unknown keywords are ignored. */
interface JsonSchemaLike {
  readonly type?: string | readonly string[];
  readonly properties?: Readonly<Record<string, JsonSchemaLike>>;
  readonly items?: JsonSchemaLike;
  readonly enum?: readonly unknown[];
}

export interface ConfigSchemaFormProps {
  /** JSON Schema of the config object (z.toJSONSchema output). */
  schema: unknown;
  value: Readonly<Record<string, unknown>>;
  /** Names offered by the skipSteps picker — empty hides the skipSteps field entirely. */
  skippableSteps?: readonly string[];
  /** Keys handled by dedicated sections elsewhere. */
  excludedKeys?: readonly string[];
  /** Field labels keyed by property name — unlisted keys fall back to the raw key. */
  labels?: Readonly<Record<string, string>>;
  className?: string;
  testId?: string;
  onChange(next: Record<string, unknown>): void;
}

const schemaType = (schema: JsonSchemaLike): string | undefined => {
  const { type } = schema;
  if (Array.isArray(type)) {
    return typeof type[0] === 'string' ? type[0] : undefined;
  }
  return typeof type === 'string' ? type : undefined;
};

/** Editable field keys of a config schema — the form renders nothing when this is empty. */
export const getConfigSchemaFormKeys = (
  schema: unknown,
  skippableSteps: readonly string[] = [],
  excludedKeys: readonly string[] = []
): string[] => {
  const properties = (schema as JsonSchemaLike | null | undefined)?.properties ?? {};
  // skipSteps disappears entirely when the plugin has nothing skippable.
  return Object.keys(properties).filter(
    key => !excludedKeys.includes(key) && (key !== 'skipSteps' || skippableSteps.length > 0)
  );
};

/** Config form fields derived from the config's own JSON Schema — not inferred from values. */
export function ConfigSchemaForm({
  schema,
  value,
  skippableSteps = [],
  excludedKeys = [],
  labels = {},
  className,
  testId,
  onChange
}: ConfigSchemaFormProps) {
  const properties = (schema as JsonSchemaLike | null | undefined)?.properties ?? {};
  const keys = getConfigSchemaFormKeys(schema, skippableSteps, excludedKeys);

  if (keys.length === 0) {
    return null;
  }

  const setField = (key: string, fieldValue: unknown): void => {
    onChange({ ...value, [key]: fieldValue });
  };

  return (
    <div data-testid={testId} className={cn('flex flex-col gap-md', className)}>
      {keys.map(key => {
        const fieldSchema = properties[key];
        const type = schemaType(fieldSchema);
        const fieldValue = value[key];
        const label = labels[key] ?? key;

        // skipSteps only accepts the plugin's skippable steps — a picker, not free text.
        if (key === 'skipSteps') {
          const selected = Array.isArray(fieldValue) ? fieldValue.map(String) : [];
          return (
            <InputWrapper key={key} label={label} className='gap-xs'>
              <div className='flex flex-col gap-xs rounded-md border border-border-base p-sm'>
                {skippableSteps.map(stepName => (
                  <Toggle
                    key={stepName}
                    checked={selected.includes(stepName)}
                    onChange={checked =>
                      setField(
                        key,
                        checked
                          ? [...selected, stepName]
                          : selected.filter(name => name !== stepName)
                      )
                    }
                    labelText={stepName}
                  />
                ))}
              </div>
            </InputWrapper>
          );
        }

        if (type === 'boolean') {
          return (
            <Toggle
              key={key}
              checked={Boolean(fieldValue)}
              onChange={checked => setField(key, checked)}
              labelText={label}
            />
          );
        }

        if (type === 'number' || type === 'integer') {
          return (
            <InputWrapper key={key} label={label} className='gap-xs'>
              <Input
                type='number'
                value={typeof fieldValue === 'number' ? fieldValue : ''}
                onChange={event => {
                  const parsed = Number(event.target.value);
                  if (event.target.value === '' || Number.isNaN(parsed)) {
                    // Clearing removes the key — the schema default applies on save.
                    onChange(omit(value, [key]));
                  } else {
                    setField(key, parsed);
                  }
                }}
              />
            </InputWrapper>
          );
        }

        if (type === 'string' && fieldSchema.enum !== undefined) {
          const options = fieldSchema.enum.map(option => ({
            value: String(option),
            label: String(option)
          }));
          return (
            <InputWrapper key={key} label={label} className='gap-xs'>
              <Select
                value={typeof fieldValue === 'string' ? fieldValue : ''}
                options={options}
                onChange={selected => setField(key, String(selected))}
              />
            </InputWrapper>
          );
        }

        if (type === 'string') {
          return (
            <InputWrapper key={key} label={label} className='gap-xs'>
              <ExpandableTextarea
                label={label}
                value={typeof fieldValue === 'string' ? fieldValue : ''}
                onChange={next => setField(key, next)}
              />
            </InputWrapper>
          );
        }

        if (type === 'array' && schemaType(fieldSchema.items ?? {}) === 'string') {
          const items = Array.isArray(fieldValue) ? fieldValue : [];
          return (
            <InputWrapper key={key} label={label} className='gap-xs'>
              <Textarea
                size='full'
                rows={3}
                value={items.join('\n')}
                // Raw lines while typing (Enter must survive the round-trip); prune on blur.
                onChange={event => setField(key, event.target.value.split('\n'))}
                onBlur={event =>
                  setField(
                    key,
                    event.target.value
                      .split('\n')
                      .map(line => line.trim())
                      .filter(Boolean)
                  )
                }
              />
            </InputWrapper>
          );
        }

        // Nested objects/arrays-of-objects — read-only until a config actually needs them.
        return (
          <InputWrapper key={key} label={label} className='gap-xs'>
            <code className='text-xs break-all text-foreground-muted'>
              {JSON.stringify(fieldValue)}
            </code>
          </InputWrapper>
        );
      })}
    </div>
  );
}

ConfigSchemaForm.displayName = 'ConfigSchemaForm';
